import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Grid } from '@mui/material';
import { DeleteOutline, DescriptionOutlined, MoreVertOutlined, PictureAsPdfOutlined } from '@mui/icons-material';
import classnames from 'classnames';
import IconButton from '@mui/material/IconButton';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';

import navigationPaths from 'engine/navigationPaths';
import { downloadFile } from 'utility/downloadFile';
import {
  useDeleteAttachment,
  useDeleteAttachments,
  useGetCostsInvoicingAttachments,
  usePostAttachmentsSend,
} from 'api';
import urls from 'engine/urls';

import ToastMessage from 'components/toastMessage';
import { SendDocumentsModal } from '../SendDocumentsModal/SendDocumentsModal';
import { DeleteConfirmationModal } from 'components/containers';
import { Checkbox, MassActions } from 'components/ui';
import { resources } from 'oems/resources';

import * as Styled from './GridView.styles';

export const GridView = ({ filters }) => {
  const navigate = useNavigate();

  const [popoverData, setPopoverData] = useState({});
  const [deleteAttachmentData, setDeleteAttachmentData] = useState(null);
  const [sendAttachmentData, setSendAttachmentData] = useState(null);
  const [toastData, setToastData] = useState(null);
  const [selectedAttachments, setSelectedAttachments] = useState([]);
  const [isDeleteAttachmentsModal, setIsDeleteAttachmentsModal] = useState(false);
  const [isSendAttachmentsModal, setIsSendAttachmentsModal] = useState(false);

  const { data: costAttachmentsData, refetch: refetchAttachmentsData } = useGetCostsInvoicingAttachments({
    from: dayjs.utc(filters.fromDate).toISOString(),
    to: dayjs.utc(filters.toDate).endOf('day').toISOString(),
    limit: 999,
    offset: 0,
  });

  const { mutate: deleteAttachments, isLoading: isDeletingAttachments } = useDeleteAttachments({
    onSuccess: () => {
      setSelectedAttachments([]);
      refetchAttachmentsData();
      setIsDeleteAttachmentsModal(false);
      setToastData({ severity: 'success', message: 'Dokumenty usunięte' });
    },
    onError: () => {
      setToastData({ severity: 'error', message: 'Nie udało się usunąć dokumentów' });
    },
  });

  const { mutate: deleteAttachment, isLoading: isAttachmentDeleting } = useDeleteAttachment({
    onSuccess: () => {
      refetchAttachmentsData();
      setDeleteAttachmentData(null);
      setSelectedAttachments([]);
      setToastData({ severity: 'success', message: 'Dokument usunięty' });
    },
    onError: () => {
      setToastData({ severity: 'error', message: 'Nie udało się usunąć dokumentu' });
    },
  });

  const { mutate: sendAttachments, isLoading: isAttachmentsSending } = usePostAttachmentsSend({
    onSuccess: () => {
      setSendAttachmentData(null);
      setSelectedAttachments([]);
      setIsSendAttachmentsModal(false);
      setToastData({ severity: 'success', message: 'Dokumenty wysłane' });
    },
    onError: () => {
      setToastData({ severity: 'error', message: 'Nie udało się wysłać dokumentów' });
    },
  });

  return (
    <Styled.Wrapper>
      {costAttachmentsData && costAttachmentsData.length === 0 && <Styled.EmptyList />}

      {selectedAttachments.length > 0 && (
        <MassActions
          items={[
            {
              key: 'deleteAttachments',
              label: 'Usuń zaznaczone',
              onClick: () => setIsDeleteAttachmentsModal(true),
            },
            {
              key: 'sendAttachments',
              label: 'Wyślij zaznaczone',
              onClick: () => setIsSendAttachmentsModal(true),
            },
          ]}
        />
      )}

      <Grid container spacing={2}>
        {(costAttachmentsData || []).map((attachment) => (
          <Grid item key={attachment.id} xs={12} sm={6} xl={4} xxl={3}>
            <Styled.Attachment
              className={classnames('actions', {
                current:
                  popoverData.id === attachment.id ||
                  selectedAttachments.some((selectedAttachment) => attachment.id === selectedAttachment.id),
              })}
              onClick={() => {
                navigate(
                  `${navigationPaths().CostInvoicesFormPreview.replace(
                    ':costId',
                    attachment.documentId,
                  )}?attachmentId=${attachment.id}`,
                );
              }}
            >
              <Styled.Icon>
                <DescriptionOutlined />
              </Styled.Icon>

              <Styled.Filename className="filename">{attachment.fileName}</Styled.Filename>

              <div
                className="actions"
                onClick={(event) => {
                  event.stopPropagation();
                }}
              >
                <Checkbox
                  checked={selectedAttachments.some((selectedAttachment) => attachment.id === selectedAttachment.id)}
                  onChange={() =>
                    setSelectedAttachments(
                      selectedAttachments.some((selectedAttachment) => attachment.id === selectedAttachment.id)
                        ? selectedAttachments.filter((selectedAttachment) => selectedAttachment.id !== attachment.id)
                        : [...selectedAttachments, attachment],
                    )
                  }
                />

                <IconButton
                  onClick={(event) => {
                    event.stopPropagation();
                    setPopoverData({ anchorEl: event.currentTarget, id: attachment.id });
                  }}
                >
                  <MoreVertOutlined />
                </IconButton>

                {!!popoverData.anchorEl && popoverData.id === attachment.id && (
                  <Styled.Popover
                    open
                    onClose={(event) => {
                      event.stopPropagation();
                      setPopoverData({});
                    }}
                    onClick={(event) => {
                      event.stopPropagation();
                    }}
                    anchorEl={popoverData.anchorEl}
                    anchorOrigin={{
                      vertical: 'bottom',
                      horizontal: 'right',
                    }}
                    transformOrigin={{
                      vertical: 'top',
                      horizontal: 'right',
                    }}
                  >
                    <Styled.DropdownContent>
                      <Styled.Menu>
                        <Styled.MenuButton
                          onClick={() => {
                            setPopoverData({});
                            setSendAttachmentData(attachment);
                          }}
                        >
                          <resources.EmailEditIcon />
                          Wyślij
                        </Styled.MenuButton>

                        <Styled.MenuButton
                          onClick={() => {
                            setPopoverData({});
                            downloadFile(
                              `${urls().root}${attachment.uri}`,
                              attachment.contentType,
                              attachment.fileName,
                            );
                          }}
                        >
                          <PictureAsPdfOutlined />
                          Pobierz załącznik
                        </Styled.MenuButton>

                        <Styled.MenuButton
                          className="danger"
                          onClick={() => {
                            setPopoverData({});
                            setDeleteAttachmentData(attachment);
                          }}
                        >
                          <DeleteOutline />
                          Usuń
                        </Styled.MenuButton>
                      </Styled.Menu>
                    </Styled.DropdownContent>
                  </Styled.Popover>
                )}
              </div>
            </Styled.Attachment>
          </Grid>
        ))}
      </Grid>

      {!!deleteAttachmentData && (
        <DeleteConfirmationModal
          onClose={() => setDeleteAttachmentData(null)}
          onConfirm={() => deleteAttachment({ id: deleteAttachmentData.id, filename: deleteAttachmentData.fileName })}
          title="Usunąć dokument?"
          message="Czy na pewno chcesz usunąć dokument? Tej operacji nie będzie można cofnąć."
          isSaving={isAttachmentDeleting}
        />
      )}

      {!!sendAttachmentData && (
        <SendDocumentsModal
          onClose={() => setSendAttachmentData(null)}
          documentsCount={1}
          onConfirm={(params) => sendAttachments({ ...params, ids: [sendAttachmentData.id] })}
          isSending={isAttachmentsSending}
        />
      )}

      {isDeleteAttachmentsModal && (
        <DeleteConfirmationModal
          onClose={() => setIsDeleteAttachmentsModal(false)}
          onConfirm={() =>
            deleteAttachments({
              attachments: selectedAttachments.map((selectedAttachment) => ({
                fileId: selectedAttachment.id,
                filename: selectedAttachment.fileName,
              })),
            })
          }
          title="Usunąć zaznaczone dokumenty?"
          message="Czy na pewno chcesz usunąć zaznaczone dokumenty? Tej operacji nie będzie można cofnąć."
          isSaving={isDeletingAttachments}
        />
      )}

      {isSendAttachmentsModal && (
        <SendDocumentsModal
          onClose={() => setIsSendAttachmentsModal(false)}
          documentsCount={selectedAttachments.length}
          onConfirm={(params) =>
            sendAttachments({ ...params, ids: selectedAttachments.map((selectedAttachment) => selectedAttachment.id) })
          }
          isSending={isAttachmentsSending}
        />
      )}

      {!!toastData && (
        <ToastMessage
          open
          onClose={() => setToastData(null)}
          severity={toastData.severity}
          message={toastData.message}
        />
      )}
    </Styled.Wrapper>
  );
};

GridView.propTypes = {
  filters: PropTypes.object.isRequired,
};
